import { motion } from 'framer-motion';
import { NikoProjectExtended } from '../../types/project';

interface ProjectHeroProps {
  project: NikoProjectExtended;
}

export function ProjectHero({ project }: ProjectHeroProps) {
  const getHeroImage = () => {
    if (project.heroImage) return project.heroImage;

    const colors: Record<string, string> = {
      'Software & KI': '8b5cf6',
      Automotive: '1C69D4',
      Energie: '10b981',
      'IT-Security': 'ef4444',
      Chemie: '84cc16',
      Pharma: 'ec4899',
    };
    const color = project.industry ? colors[project.industry] || '10b981' : '10b981';
    return `https://placehold.co/1600x900/${color}/0a0a0a?text=${encodeURIComponent(project.company)}`;
  };

  return (
    <section className="relative min-h-[70vh] flex items-end pb-16 pt-32">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src={getHeroImage()}
          alt={project.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 px-8 md:px-12 lg:px-24 max-w-7xl mx-auto w-full">
        <div className="space-y-6">
          {/* Meta info */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap items-center gap-4"
          >
            {project.current && (
              <span className="px-3 py-1 text-xs bg-emerald-500/20 text-emerald-500 rounded-full tracking-wider uppercase">
                Aktuell
              </span>
            )}
            <span className="text-sm text-white/40">{project.period}</span>
            {project.industry && (
              <>
                <span className="text-white/20">•</span>
                <span className="text-sm text-emerald-500/80">{project.industry}</span>
              </>
            )}
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-4xl md:text-6xl lg:text-7xl font-light text-white leading-tight"
          >
            {project.title}
          </motion.h1>

          {/* Company */}
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xl md:text-2xl text-emerald-500"
          >
            {project.company}
          </motion.p>
        </div>
      </div>
    </section>
  );
}
